import { useUpdateRecoveryConfigMutation } from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import { MutableRefObject, useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { AGENT_TUNING_PATH } from '../routes'

// AI-121/AI-175: Save-and-test routes to the live agent simulator on a
// successful save. The simulator's R12 home is the Agent Tuning tab, so push to
// the shared AGENT_TUNING_PATH (no `/recovery_ai/sandbox` route exists; use
// react-router-redux push). routes.ts is the single owner of that literal.

type UseSaveAndTest = {
  handleSave: () => Promise<void>
  handleSaveAndTest: () => Promise<void>
  clearSuccess: () => void
  saveSuccess: boolean
  isSaving: boolean
  isSaveError: boolean
  saveError: unknown
  // True when there are unsaved edits and no save is in flight — drives the
  // Save button's `disabled` (a pristine form has nothing to persist).
  canSave: boolean
  // Flipped just before Save-and-test's own navigation so the route-leave guard
  // can let that one sanctioned transition through. See useUnsavedChangesPrompt.
  guardBypassRef: MutableRefObject<boolean>
}

// Shared Save / Save-and-test orchestration for the RecoveryAI Settings panes
// (Brand voice, Escalation rules, Recovery generosity, Approved rewards). Each
// pane keeps its own local field state and passes a `buildPayload` that derives
// the partial config to persist plus its own `isDirty`; this hook owns the
// mutation, the one-shot success flag, the error/loading flags, the dirty-gating,
// and the post-save navigation (incl. the unsaved-changes-guard bypass).
export function useSaveAndTest(
  buildPayload: () => Partial<RecoveryConfig>,
  isDirty: boolean,
  // AI-576 (F3): OPT-IN post-save verification. When supplied, the echoed config
  // from the PUT is compared against the exact submitted payload; a mismatch
  // (a 2xx that didn't actually persist the field — e.g. a pre-migration backend
  // that drops an unpermitted param and no-ops the update) is treated as a save
  // FAILURE instead of a false "Saved" banner. Omitted by every other pane, so
  // their behavior is byte-for-byte unchanged (resolve = success).
  verifySaved?: (
    saved: RecoveryConfig,
    submitted: Partial<RecoveryConfig>
  ) => boolean
): UseSaveAndTest {
  const dispatch = useDispatch()
  const [
    updateConfig,
    { isError: isMutationError, isLoading: isSaving, reset, error: saveError },
  ] = useUpdateRecoveryConfigMutation()

  // Whether the last Save (not Save-and-test, which navigates away) succeeded.
  // Drives the positive success Notification; cleared at the start of every save
  // attempt and on every edit so a stale "Saved" banner never lingers.
  const [saveSuccess, setSaveSuccess] = useState(false)

  // AI-576 (F3): set when the opt-in verifySaved rejected the echoed config.
  // OR'd into the returned isSaveError below so it surfaces the same negative
  // banner as an HTTP error; cleared at the start of every save attempt.
  const [verifyFailed, setVerifyFailed] = useState(false)

  // AI-174 (Bug 1): the route-leave <Prompt> reads `isDirty`, which compares
  // local state against the RTK Query cache — and the cache only refreshes after
  // the save's refetch round-trips. So at the instant Save-and-test navigates,
  // `isDirty` can still be true even though the PUT already succeeded, firing a
  // bogus "unsaved changes" confirm. Flip this ref before the sanctioned
  // navigation; useUnsavedChangesPrompt lets that transition through.
  const guardBypassRef = useRef(false)

  // AI-174 (Bug 2): Save is actionable only with unsaved edits and no in-flight
  // save — drives a REAL `disabled` so the button never looks clickable-but-dead.
  const canSave = isDirty && !isSaving

  // A save "failed" either because the mutation errored (HTTP) or because the
  // opt-in verifySaved check found the echo didn't match the submission (a 2xx
  // no-op). Both surface the same negative banner.
  const isSaveError = isMutationError || verifyFailed

  // Panes call this on every edit/select: drop any stale "Saved" banner.
  // It deliberately does NOT call reset(): the panes' inputs stay enabled during
  // an in-flight save, so resetting here on a mid-save keystroke would clear the
  // mutation's isLoading flag, re-enable the (disabled) buttons, and let a second
  // concurrent PUT race the first (AI-121 R2). A stale error banner still clears
  // because reset() runs at the START of handleSave / handleSaveAndTest.
  const clearSuccess = useCallback(() => {
    setSaveSuccess(false)
  }, [])

  const handleSave = useCallback(async () => {
    // Nothing to persist on a pristine form (and the Save button is disabled) —
    // never fire a no-op PUT or a false "Saved" banner.
    if (!canSave) return
    setSaveSuccess(false)
    setVerifyFailed(false)
    reset()
    // Capture the submitted payload ONCE so a mid-save edit can't skew the echo
    // comparison (the panes leave their inputs enabled during a save).
    const submitted = buildPayload()
    try {
      const saved = await updateConfig(submitted).unwrap()
      // Opt-in verify: a 2xx that didn't persist the field surfaces the error
      // path, not a false success banner. Panes without verifySaved skip this.
      if (verifySaved && !verifySaved(saved, submitted)) {
        setVerifyFailed(true)
        return
      }
      setSaveSuccess(true)
    } catch {
      // isSaveError drives the negative Notification; stay on the pane, no nav.
    }
  }, [canSave, updateConfig, buildPayload, reset, verifySaved])

  const handleSaveAndTest = useCallback(async () => {
    if (isSaving) return
    setSaveSuccess(false)
    setVerifyFailed(false)
    // AI-174 (Bug 2): decouple "navigate to test" from "must be dirty to PUT" —
    // a pristine form skips the no-op PUT and just routes to test the
    // already-saved config.
    if (!isDirty) {
      guardBypassRef.current = true
      dispatch(push(AGENT_TUNING_PATH))
      return
    }
    reset()
    const submitted = buildPayload()
    try {
      // unwrap() rejects on HTTP error → navigation is gated on success only.
      const saved = await updateConfig(submitted).unwrap()
      // Same opt-in verify as handleSave: a dropped-field no-op must NOT navigate
      // to "test" a config that wasn't persisted.
      if (verifySaved && !verifySaved(saved, submitted)) {
        setVerifyFailed(true)
        return
      }
      // Sanctioned post-save navigation: suppress the unsaved-changes guard for
      // THIS transition (isDirty may still read true until the refetch lands).
      guardBypassRef.current = true
      dispatch(push(AGENT_TUNING_PATH))
    } catch {
      // Stay on the pane; isSaveError surfaces the error Notification.
    }
  }, [
    isSaving,
    isDirty,
    updateConfig,
    buildPayload,
    reset,
    dispatch,
    verifySaved,
  ])

  return {
    handleSave,
    handleSaveAndTest,
    clearSuccess,
    saveSuccess,
    isSaving,
    isSaveError,
    saveError,
    canSave,
    guardBypassRef,
  }
}
